<template>
  <main class="main cart-page page-inside" v-if="isAuth">
    <div class="main-wrapper">
      <section class="section cart-section">
        <div class="wrapper">
          <div class="title">{{$t("Cart")}}</div>
          <div class="desc empty-desc" v-if="!cartContents.length">
            Cart is empty!
          </div>
          <div class="left" v-if="cartContents.length">
            <div class="product-list">
              <div class="item" v-for="(item, i) in cartContents" :key="i">
                <div class="preview">
                  <img :src="getImageUrl(item)" class="img"/>
                </div>
                <div class="title">{{item.item.title}}</div>
                <div class="item-end">
                  <div class="count">
                    <div class="count__item minus" @click="removeOneFromCart(item)"></div>
                    <input type="number" value="1" v-model="item.count"/>
                    <div class="count__item plus" @click="addToCart(item)"></div>
                  </div>
                  <div class="price">{{item.item.price}} <span class="currency">{{ currencySymbol }}</span></div>
                </div>
                <div class="delete" @click="removeFromCart(item)">
                  <div class="desc">{{$t("Delete item")}}</div>
                </div>
              </div>
            </div>
          </div>
          <div class="right" v-if="cartContents.length">
            <div class="wrapper form">
              <div class="title small">{{$t("Contact information")}}</div>
              <div class="input-container">
                <div class="desc"><span class="red">*</span>{{$t("First name")}}:</div>
                <input type="text" :placeholder="$t('First name')" v-model="name"/>
              </div>
              <div class="input-container">
                <div class="desc"><span class="red">*</span>{{$t("Last name")}}:</div>
                <input type="text" :placeholder="$t('Last name')" v-model="surname"/>
              </div>
              <div class="input-container">
                <div class="desc"><span class="red">*</span>{{$t("Email")}}:</div>
                <input type="email" :placeholder="$t('Email')" v-model="email"/>
              </div>
              <div class="input-container">
                <div class="desc"><span class="red">*</span>{{$t("Phone")}}:</div>
                <MazPhoneNumberInput
                  v-model="phone"
                  v-model:country-code="countryCodeName"
                  :defaultCountryCode="convertedShortName"
                  show-code-on-list
                  :only-countries="countryOptionsIso"
                  @update="phoneDataUpdate($event)"
                />
              </div>
              <div class="input-container steam-input-container">
                <div class="desc"><span class="red">*</span>{{$t("Steam ID")}}:</div>
                <input type="text" :placeholder="$t('Steam ID')" v-model="steamId"/>
              </div>
             
              <div class="input-container country-select-container">
                <div class="desc"><span class="red">*</span>{{ $t("Country") }}:</div>
                <select v-model="countryId" :class="{ 'empty': !countryId }">
                  <option value="" disabled selected>{{ $t("Country") }}</option>
                  <option v-for="option in countryOptions" :key="option.id" :value="option.id">
                    {{ option.title }}
                  </option>
                </select>
              </div>
              
              <div class="input-container">
                <div class="desc"><span class="red">*</span>{{$t("City")}}:</div>
                <input type="text" :placeholder="$t('City')" v-model="city"/>
              </div>
              <div class="input-container">
                <div class="desc"><span class="red">*</span>{{$t("Address")}}:</div>
                <input type="text" :placeholder="$t('Address')" v-model="address"/>
              </div>
              <div class="input-container">
                <div class="desc"><span class="red">*</span>{{$t("Post Code")}}:</div>
                <input type="text" :placeholder="$t('Post Code')" v-model="zip"/>
              </div>
              <div class="input-container">
                <div class="checkbox-container">
                  <label class="checkbox-label">
                    <div class="checkbox-label-wrapper">
                      <input type="checkbox" name="terms" v-model="terms"/>
                      <div class="checkbox"></div>
                      <span class="desc">{{$t("I agree with")}} </span>
                      <a @click="goToPage('privacy')" class="desc">{{$t("Privacy Policy")}}</a>
                      <a @click="goToPage('terms')" class="desc">{{$t("Terms of use")}}</a>
                    </div>
                  </label>
                </div>
              </div>
            </div>
            <div class="wrapper">
              <div class="input-container promocode-container row">
                <div class="desc">{{$t("Promo code")}}:</div>
                <div class="promocode-input-wrapper">
                  <div class="promocode-input-container">
                    <input 
                      type="text" 
                      :placeholder="$t('Enter promo code')" 
                      v-model="promocodeInput"
                      :disabled="promocodeApplied"
                      class="promocode-input"
                    />
                    <button 
                      v-if="promocodeApplied"
                      @click="handleRemovePromocode"
                      class="promocode-remove-button"
                      title="Remove promo code"
                    >
                      <img src="@/assets/img/close.svg" alt="Remove" />
                    </button>
                  </div>
                  <button 
                    @click="handleApplyPromocode" 
                    class="button promocode-button"
                    :disabled="!promocodeInput || promocodeApplied || promocodeLoading"
                  >
                    <span>{{ promocodeApplied ? $t("Applied") : $t("Apply") }}</span>
                  </button>
                </div>
                <Transition>
                  <div class="desc success-message" v-if="promocodeSuccess">{{promocodeSuccess}}</div>
                </Transition>
                <Transition>
                  <div class="desc error-message" v-if="promocodeError">{{promocodeError}}</div>
                </Transition>
              </div>
            </div>
            
            <div class="wrapper">
              <div class="row" v-if="hasDiscount">
                <div class="desc">{{$t("Subtotal")}}:</div>
                <div class="title">{{totalSum}} <span class="currency">{{ currencySymbol }}</span></div>
              </div>
              <div class="row discount-row" v-if="hasDiscount">
                <div class="desc">{{$t("Discount")}}:</div>
                <div class="title discount-amount">-{{discountAmount.toFixed(2)}} <span class="currency">{{ currencySymbol }}</span></div>
              </div>
              <div class="row">
                <div class="desc">{{$t("Total to pay")}}:</div>
                <div class="title">{{finalTotal}} <span class="currency">{{ currencySymbol }}</span></div>
              </div>
              <button @click="submit" :class="['button', {'disabled': !requiredFieldsAreFilled}]">
                <span>{{$t("Confirm")}}</span>
              </button>
              <Transition>
                <div class="desc error-message" v-if="error">{{error}}</div>
              </Transition>
            </div>
          </div>
        </div>
      </section>
    </div>
  </main>
</template>
<script>
import { mapGetters, mapActions } from 'vuex';

export default {
  name: 'CartPage',
  props: {
    isAuth: {
      type: Boolean,
      required: true
    },
    currencySymbol: {
      type: String,
      required: true
    },
    cartContents: {
      type: Array,
      required: true
    },
    totalSum: {
      type: String,
      required: true
    },
  },
  data: function() {
    return {
      imgDomain: '',
      countryOptions: [],
      countryOptionsId: [],
      name: '',
      surname: '',
      email: '',
      phone: '',
      steamId: '',
      countryId: '',
      city: '',
      address: '',
      zip: '',
      terms: false,
      phoneFull: "",
      convertedShortName: '',
      countryCode: '',
      countryCodeList: [],
      profilePhone: '',
      countryOptionsIso: [],
      countryCodeName: '',
      promocodeInput: '',
      error: ''
    }
  },
  mounted() {
    this.imgDomain = process.env.VUE_APP_STATIC_DOMAIN;
    this.getProfile();
    this.getCounties();
  },
  beforeUnmount() {
    this.resetPromocode();
  },
  computed: {
    ...mapGetters('promocode', {
      promocode: 'promocode',
      promocodeApplied: 'promocodeApplied',
      discountAmount: 'discountAmount',
      promocodeLoading: 'loading',
      promocodeError: 'error',
      promocodeSuccess: 'success',
      hasDiscount: 'hasDiscount',
    }),
    requiredFieldsAreFilled() {
      return !!(
        this.name && this.surname && this.email && this.phone && 
        this.steamId && this.countryId && 
        this.city && this.address && this.zip && this.terms
      );
    },
    finalTotal() {
      const total = parseFloat(this.totalSum) || 0;
      const discount = this.discountAmount || 0;
      return (total - discount).toFixed(2);
    }
  },
  methods: {
    ...mapActions('promocode', ['applyPromocode', 'setPromocode', 'resetPromocode']),
    async handleApplyPromocode() {
      await this.setPromocode(this.promocodeInput);
      await this.applyPromocode();
    },
    handleRemovePromocode() {
      this.promocodeInput = '';
      this.resetPromocode();
    },
    getImageUrl(item) {
      if (item.item.item_type == 'pack') {
        return item.item.img_url;
      } else {
        return this.imgDomain + item.item.img_url;
      }
    },
    goToPage(id) {
      this.$emit('goToPage', id);
    },
    getProfile() {
      let self = this;
      this.$http.get('user/profile')
      .then((res) => {
        if (res.status == 200) {
          self.email = res.data.email;
          self.name = res.data.name;
          self.surname = res.data.surname;
          self.phone = res.data.phone.replace(/^[^\s]*\s/, '');
          self.profilePhone = res.data.phone;
          self.address = res.data.address;
          self.city = res.data.city;
          self.zip = res.data.zip;
          self.countryId = res.data.country;
          self.steamId = res.data.steamId;
          self.getAllCountryCodes();
        }
      })
      .catch((res) => {
        if(res.response.status === 401) {
          this.openSignIn();
        }
      })
    },
    getAllCountryCodes() {
      this.$http.get('phone-codes')
      .then((res) => {
        if (res.data.status == "OK") {
          this.countryCodeList = res.data.payload; 
          this.countryCode = this.getSymbolsBeforeSpace(this.profilePhone).replace( '+' ,'');
          this.getShortNameByPhoneCode(this.countryCode, this.countryCodeList);
          this.countryCodeName = this.convertedShortName;
        }
      })
      .catch((res) => {
        if(res.response.status === 401) {
          this.$emit('openSignIn');
        }
      })
    },
    getShortNameByPhoneCode(phoneCode, codeList) {
      for (let i = 0; i < codeList.length; i++) {
        const countryCode = Object.keys(codeList[i])[0];
        const codeValue = codeList[i][countryCode].code
        if (codeValue == phoneCode) {
          this.convertedShortName = countryCode;
        }
      }
    },
    getSymbolsBeforeSpace(inputString) {
      const regex = /^[^\s]+/;
      const match = inputString.match(regex);

      if (match) {
          return match[0];
      } else {
          return ""; // Return an empty string if no symbols are found
      }
    },
    phoneDataUpdate(e) {
      this.phoneFull = e.formatInternational;
    },
    submit() {
      let data = {
        "name": this.name,
        "surname": this.surname,
        "email": this.email,
        "phone": this.phoneFull,
        "steamId": this.steamId,
        "country": this.countryId,
        "city": this.city,
        "address": this.address,
        "postCode": this.zip
      }
      
      if (this.promocodeApplied && this.promocode) {
        data.promocode = this.promocode;
      }
      
      this.$emit('openOrderModal', data);
    },
    getCounties() {
      this.$http.get('countries')
      .then((res) => {
          this.countryOptions = res.data.payload;
          this.countryOptionsIso = res.data.payload.map(obj => obj.iso);
      })
      .catch((res) => {
        if(res.response.status === 401) {
          this.openSignIn();
        }
      })
    },
    removeFromCart(item) {
      this.$emit('removeFromCart', item, item.item_id, item.item.item_type);
    },
    removeOneFromCart(item) {
      this.$emit('removeOneFromCart', item, item.item_id, item.item.item_type);
    },
    addToCart(item) {
      this.$emit('addToCart', item.item_id, item.item.item_type)
    }
  }
}
</script>

<style scoped>
.promocode-container {
  margin-bottom: 0 !important;
}

.promocode-input-wrapper {
  display: flex;
  gap: 12px;
  align-items: stretch;
}

.promocode-input-container {
  position: relative;
  flex: 1;
  min-width: 0;
}

.promocode-input {
  width: 100%;
  padding-right: 45px;
}

.promocode-remove-button {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0.6;
  transition: opacity 0.3s;
}

.promocode-remove-button:hover {
  opacity: 1;
}

.promocode-remove-button img {
  width: 16px;
  height: 16px;
  filter: brightness(0);
}

.promocode-button {
  width: auto !important;
  min-width: 120px;
  padding: 16px 24px;
  flex-shrink: 0;
  margin: 0 !important;
}

.success-message {
  color: #08A714 !important;
  margin-top: 8px;
  font-size: 14px;
}

.error-message {
  color: #ef4444 !important;
  margin-top: 8px;
  font-size: 14px;
}

.discount-row {
  margin-top: 16px !important;
}

.discount-amount {
  color: #08A714 !important;
}

@media screen and (max-width: 768px) {
  .promocode-input-wrapper {
    flex-direction: column;
    gap: 8px;
  }
  
  .promocode-button {
    width: 100% !important;
  }
}
</style>